import React, { useState } from "react";
import { Card, Form, Input, Button, Typography, Switch } from "antd";
import { toast } from 'react-toastify';
import { MailOutlined, BulbOutlined, UserOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "Common/Client/Interceptor";
import { useTheme } from "Common/hooks/context/ThemeProvider";

const { Title, Text } = Typography;

const BASE_URL = process.env.REACT_APP_API_URL;
const API = {
  account: {
    register: `${BASE_URL}/Account/Individual/Register`,
  },
};

const Register = () => {
  const [loading, setLoading] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const payload = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
      };

      console.log("Registration Payload:", payload);

      const response = await api.post(API.account.register, payload);

      // Log the complete response
      console.log("Full API Response:", response);
      console.log("Response Status:", response.status);
      console.log("Response Data:", response.data);
      console.log("Response Headers:", response.headers);

      // Check if response actually indicates success
      if (response.status === 200 || response.status === 201) {
        toast.success("Registration successful! Please check your email for verification.");
        navigate("/registration-success");
      } else {
        toast.warning("Registration completed but may require verification.");
      }

    } catch (error) {
      console.error("Registration error details:", {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers
      });

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0] ||
        "Registration failed!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        ...styles.container,
        backgroundColor: darkMode ? "#1e1e2f" : "#f4f6f9",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ width: "100%", maxWidth: "700px" }}
      >
        <Card
          style={{
            ...styles.card,
            backgroundColor: darkMode ? "#2d2d3a" : "#fff",
            color: darkMode ? "#fff" : "#000",
          }}
          bordered={false}
        >
          <div style={styles.header}>
            <Title level={3} style={{ color: darkMode ? "#fff" : "#1890ff" }}>
              Create an Account
            </Title>
            <div style={styles.themeToggle}>
              <BulbOutlined style={{ marginRight: 8 }} />
              <Switch checked={darkMode} onChange={toggleTheme} />
            </div>
          </div>

          <Text
            type="secondary"
            style={{
              display: "block",
              marginBottom: 24,
              color: darkMode ? "#ccc" : undefined,
            }}
          >
            Please fill in the details to register
          </Text>

          <Form name="register-form" layout="vertical" onFinish={handleRegister}>
            <Form.Item
              name="firstName"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>First Name</span>}
              rules={[{ required: true, message: "Please enter your first name!" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="John" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Last Name</span>}
              rules={[{ required: true, message: "Please enter your last name!" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Doe" />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Email</span>}
              rules={[
                { required: true, message: "Please enter your email!" },
                { type: "email", message: "Please enter a valid email address!" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="example@email.com" />
            </Form.Item>

            <Form.Item>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    backgroundColor: darkMode ? "#226c87c7" : "#0c313f",
                    borderColor: darkMode ? "#226c87c7" : "#0c313f",
                    color: "#fff",
                  }}
                >
                  {loading ? "Registering..." : "Register"}
                </Button>
              </motion.div>
            </Form.Item>
          </Form>

          <Text type="secondary" style={{ color: darkMode ? "#aaa" : undefined }}>
            Already have an account?{" "}
            <Button type="link" onClick={() => navigate("/login")}>
              Log In
            </Button>
          </Text>
        </Card>
      </motion.div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    transition: "background-color 0.3s ease",
  },
  card: {
    width: "100%",
    maxWidth: "700px",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    transition: "background-color 0.3s ease, color 0.3s ease",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  themeToggle: {
    display: "flex",
    alignItems: "center",
  },
};

export default Register;